import React, { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { createMarkup } from '@agria/utils/src/createMarkup';
import { Container } from '../container/Container';
import { useBackgroundColor } from '../../utils';
import { Typography } from '../typography/Typography';
import { LogoInverse } from '../svg';
import { Stack } from '../stack/Stack';
import { Divider } from '../divider/Divider';
import { LinkTo } from '../linkTo/LinkTo';
import { SslSecure } from '../svg/svgs/SslSecure';
import { Facebook, Twitter, Instagram, LinkedIn } from '../icon';
import tokens from './tokens';
import { CookiePreferenceButton } from '../cookiePreferenceButton/CookiePreferenceButton';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { ButtonPinReveal } from '../buttonPinReveal/ButtonPinReveal';

// Interface

interface FooterProps {
  brandMessage?: string;
  smallText?: string;
  navigationColumns?: FooterMenuProps[];
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    linkedIn?: string;
    pinterest?: string;
    twitter?: string;
    youTube?: string;
  };
  secondaryNavigation?: {
    label?: string;
    target?: string;
    url?: string;
  }[];
  showCookieButton?: boolean;
}

export interface FooterMenuProps {
  header?: string;
  links?: {
    label?: string;
    url?: string;
    target?: string;
  }[];
}

// Styles

const StyledLinkBar = styled.div`
  ${({ theme: { colors, space } }) => css`
    ${useBackgroundColor('brandTwo100')};
    padding: ${space.fluidLarge} 0;

    a,
    button {
      color: ${colors[(tokens.linkColour as ThemeColorsKeys) || 'brandOne']};
    }
  `};
`;

const StyledFooter = styled.footer<{ hasLinkBar?: boolean }>`
  ${({ theme: { colors, space }, hasLinkBar }) => css`
    position: relative;
    ${useBackgroundColor(tokens.backgroundColour as ThemeColorsKeys)};
    padding: ${hasLinkBar ? space.fluidLarge : space.fluidXxLarge} 0;

    ${tokens.ruffleHeight &&
    tokens.ruffleBackgroundImage &&
    tokens.ruffleBackgroundSize &&
    css`
      &:before {
        display: block;
        position: absolute;
        content: '';
        width: 100%;
        height: ${tokens.ruffleHeight};
        top: -${tokens.ruffleHeight};
        color: ${colors[tokens.backgroundColour]};
        background-image: ${tokens.ruffleBackgroundImage};
        background-size: ${tokens.ruffleBackgroundSize};
        background-position: center;
      }
    `}

    a,
    button:not(#surfly) {
      text-decoration: none;
      color: ${colors[(tokens.linkColour as ThemeColorsKeys) || 'brandOne']};

      &:hover {
        text-decoration: ${tokens.linkHoverDecoration || 'none'};
      }
    }
  `};
`;

const StyledBrandBar = styled.div`
  ${({ theme: { space, media } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.xSmall};

    @media (min-width: ${media.medium}) {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
  `};
`;

const StyledLogos = styled.div`
  ${({ theme: { space } }) => css`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    gap: ${space.fluidxxSmallToSmall};
    align-items: flex-start;
  `};
`;

const StyledSocialLinks = styled.ul`
  ${({ theme: { space, colors } }) => css`
    display: flex;
    gap: ${space.xxSmall};

    a {
      display: inline-block;
      color: ${colors.utilityWhite};

      &:focus-visible {
        outline: 4px solid ${colors.systemFocus};
      }
    }
  `};
`;

const StyledSmallLinks = styled.ul`
  ${({ theme: { space, media, colors } }) => css`
    display: flex;
    flex-direction: column;
    gap: ${space.xxSmall};

    a,
    button: {
      color: ${colors[tokens.smallTextColor as ThemeColorsKeys] ||
      colors.brandOne20};
      text-decoration: underline;

      &:hover {
        text-decoration: underline;
        color: ${colors.utilityWhite};
      }
    }

    @media (min-width: ${media.large}) {
      flex-direction: row;
      gap: ${space.small};

      li + li {
        border-left: solid 1px
          ${colors[tokens.linkDividerColor as ThemeColorsKeys] ||
          colors.brandOne80};
        padding-left: ${space.small};
      }
    }
  `};
`;

const StyledDiv = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    display: flex;
    row-gap: ${space.medium};

    @media (max-width: ${maxMedia.large}) {
      flex-direction: column;
    }
  `};
`;

const StyledMenus = styled.div`
  ${({ theme: { space, media, colors } }) => css`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: ${space.fluidxxSmallToSmall};

    @media (min-width: ${media.large}) {
      flex-direction: row;
      justify-content: space-between;

      &:after {
        content: '';
      }
    }

    a,
    button {
      &:hover,
      &:focus-visible {
        color: ${colors.utilityWhite};
      }
    }
  `};
`;

const StyledIcon = styled.div`
  svg,
  img {
    width: 48px;
    height: 48px;
  }
`;

const StyledSslSecureContainer = styled.div`
  ${({ theme: { media } }) => css`
    width: 125px;

    @media (min-width: ${media.small}) {
      width: 167px;
    }
  `};
`;

const StyledSmallText = styled(Typography)`
  ${({ theme: { space } }) => css`
    p + p {
      margin-top: ${space.small};
    }
  `};
`;

// JSX

export const FooterMenu = ({ header, links }: FooterMenuProps) => (
  <Stack gap="fluidxxSmallToSmall">
    {header && (
      <Typography
        component="h2"
        variant="headingSmall"
        role="separator"
        aria-label={header}
      >
        {header}
      </Typography>
    )}
    {links?.map(({ label, url }, index) =>
      url ? (
        <StyledDiv key={index} role="listitem">
          <LinkTo to={url}>{label}</LinkTo>
        </StyledDiv>
      ) : null,
    )}
  </Stack>
);

export const Footer: FC<FooterProps> = ({
  brandMessage,
  navigationColumns,
  secondaryNavigation,
  smallText,
  socialLinks,
  showCookieButton = true,
}) => {
  const [shouldShowSurfly, setShouldShowSurfly] = useState(true);

  // Check if Surfly should be hidden on current path
  useEffect(() => {
    const checkSurflyVisibility = () => {
      const disabledPaths = process.env.GATSBY_LIVE_CHAT_DISABLED_PATHS || '';

      if (!disabledPaths) {
        setShouldShowSurfly(true);
        return;
      }

      const currentPath = window.location.pathname;
      const paths = disabledPaths.split(',').map((path) => path.trim());

      const shouldHide = paths.some((disabledPath) => {
        if (disabledPath.endsWith('*')) {
          return currentPath.startsWith(disabledPath.slice(0, -1));
        }
        return currentPath === disabledPath;
      });

      setShouldShowSurfly(!shouldHide);
    };

    // Initial check
    checkSurflyVisibility();

    // Listen for route changes
    let lastUrl = window.location.href;
    const observer = new MutationObserver(() => {
      const url = window.location.href;
      if (url !== lastUrl) {
        lastUrl = url;
        setTimeout(checkSurflyVisibility, 100);
      }
    });

    observer.observe(document, { subtree: true, childList: true });

    // Also listen for popstate events (back/forward navigation)
    const handlePopstate = () => {
      setTimeout(checkSurflyVisibility, 100);
    };
    window.addEventListener('popstate', handlePopstate);

    return () => {
      observer.disconnect();
      window.removeEventListener('popstate', handlePopstate);
    };
  }, []);

  return (
    <>
      {tokens.seperateLinkBar && secondaryNavigation?.length ? (
        <StyledLinkBar
          as="nav"
          aria-label="Policies and Cookies Navigation Menu"
        >
          <Container>
            <StyledSmallLinks>
              {showCookieButton ? (
                <Typography component="li" variant="bodySmall" key="onetrust">
                  <CookiePreferenceButton
                    buttonLabel="Manage your cookies"
                    noButtonPadding
                  />
                </Typography>
              ) : null}
              {secondaryNavigation?.map((nav, index) =>
                nav.url ? (
                  <Typography component="li" variant="bodySmall" key={index}>
                    <LinkTo to={nav.url}>{nav.label}</LinkTo>
                  </Typography>
                ) : null,
              )}
            </StyledSmallLinks>
          </Container>
        </StyledLinkBar>
      ) : null}
      <StyledFooter hasLinkBar={tokens.seperateLinkBar}>
        <Container>
          <Stack gap="fluidMedium">
            {tokens.includeInverseLogo || brandMessage ? (
              <StyledBrandBar>
                {tokens.includeInverseLogo && (
                  <LogoInverse aria-hidden="true" />
                )}
                {brandMessage && (
                  <Typography variant="bodyLarge">{brandMessage}</Typography>
                )}
              </StyledBrandBar>
            ) : null}
            {socialLinks?.facebook ||
            socialLinks?.twitter ||
            socialLinks?.linkedIn ||
            socialLinks?.instagram ||
            socialLinks?.youTube ||
            navigationColumns?.length ? (
              <>
                <Divider inverse />
                <StyledDiv as="nav" aria-label="Footer Navigation Menu">
                  {navigationColumns && (
                    <StyledMenus role="list">
                      {navigationColumns?.map((navColumn, index) => (
                        <FooterMenu
                          key={index}
                          header={navColumn?.header}
                          links={navColumn?.links}
                        />
                      ))}
                    </StyledMenus>
                  )}
                  <StyledLogos>
                    <Typography component="h3" variant="bodyMediumBold">
                      Follow us
                    </Typography>
                    <StyledSocialLinks>
                      {socialLinks?.facebook && (
                        <li>
                          <a
                            href={socialLinks?.facebook}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Facebook"
                          >
                            <StyledIcon>
                              <Facebook aria-hidden="true" />
                            </StyledIcon>
                          </a>
                        </li>
                      )}

                      {socialLinks?.twitter && (
                        <li>
                          <a
                            href={socialLinks?.twitter}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Twitter"
                          >
                            <StyledIcon>
                              <Twitter aria-hidden="true" />
                            </StyledIcon>
                          </a>
                        </li>
                      )}
                      {socialLinks?.linkedIn && (
                        <li>
                          <a
                            href={socialLinks?.linkedIn}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="LinkedIn"
                          >
                            <StyledIcon>
                              <LinkedIn aria-hidden="true" />
                            </StyledIcon>
                          </a>
                        </li>
                      )}
                      {socialLinks?.instagram && (
                        <li>
                          <a
                            href={socialLinks?.instagram}
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Instagram"
                          >
                            <StyledIcon>
                              <Instagram aria-hidden="true" />
                            </StyledIcon>
                          </a>
                        </li>
                      )}
                    </StyledSocialLinks>
                  </StyledLogos>
                </StyledDiv>
              </>
            ) : null}
            {!tokens.seperateLinkBar && <Divider inverse />}
            {secondaryNavigation &&
              secondaryNavigation.length > 0 &&
              !tokens.seperateLinkBar && (
                <StyledDiv
                  as="nav"
                  aria-label="Policies and Cookies Navigation Menu"
                >
                  <StyledSmallLinks as="ul">
                    {secondaryNavigation?.map((nav, index) =>
                      nav.url ? (
                        <Typography
                          component="li"
                          variant="bodySmall"
                          key={index}
                        >
                          <LinkTo to={nav.url}>{nav.label}</LinkTo>
                        </Typography>
                      ) : null,
                    )}
                    {showCookieButton ? (
                      <Typography
                        component="li"
                        variant="bodySmall"
                        key="onetrust"
                      >
                        <CookiePreferenceButton
                          buttonLabel="Manage your cookies"
                          noButtonPadding
                        />
                      </Typography>
                    ) : null}
                  </StyledSmallLinks>
                </StyledDiv>
              )}
            {smallText && (
              <StyledSmallText
                variant="bodySmall"
                component="div"
                color={
                  (tokens.smallTextColor as ThemeColorsKeys) || 'brandOne20'
                }
                suppressHydrationWarning
              >
                {createMarkup(
                  // Hack to remove unnecessary <br> in footer content
                  smallText
                    // Remove 1 <br> at the beginning of a paragraph
                    .replaceAll(/<p[^>]*>\s*<br[^>]*>/g, (match) =>
                      match.replace(/<br[^>]*>/, ''),
                    )
                    // Remove 1 or more <br> at the end of a paragraph
                    .replaceAll(/(<br[^>]*>\s*)+<\/p>/g, '</p>'),
                )}
              </StyledSmallText>
            )}
            <StyledSslSecureContainer>
              <SslSecure title="SSL Secure logo" />
            </StyledSslSecureContainer>
            {process.env.GATSBY_LOAD_SURFLY === 'true' && shouldShowSurfly && (
              <ButtonPinReveal />
            )}
          </Stack>
        </Container>
      </StyledFooter>
    </>
  );
};

export default Footer;
